export interface DraftAttachmentReference {
  kind: 'api_document' | 'endpoints_filter' | 'identity_feed'
  name: string
  type: string
}

export interface ConversationDraft {
  version: 1
  conversationId: string
  text: string
  attachments: DraftAttachmentReference[]
  updatedAt: string
}

export type DraftSaveResult = 'saved' | 'cleared' | 'sensitive' | 'unavailable'

const DRAFT_PREFIX = 'saviynt-agent.conversation-draft.v1:'
const MAX_DRAFT_LENGTH = 20_000

export function conversationDraftKey(conversationId: string) {
  return `${DRAFT_PREFIX}${conversationId}`
}

export function draftAppearsSensitive(text: string): boolean {
  const patterns = [
    /authorization\s*[:=]\s*bearer\s+[a-z0-9._~+/=-]{8,}/i,
    /(?:password|passwd|client_secret|api[_-]?key|access[_-]?token|refresh[_-]?token)\s*[:=]\s*["']?[^\s,"'}]{4,}/i,
    /-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/i,
  ]
  return patterns.some(pattern => pattern.test(text))
}

export function loadConversationDraft(conversationId: string, storage: Storage = localStorage): ConversationDraft | null {
  try {
    const raw = storage.getItem(conversationDraftKey(conversationId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.version !== 1 || parsed?.conversationId !== conversationId || typeof parsed.text !== 'string' || !Array.isArray(parsed.attachments)) {
      storage.removeItem(conversationDraftKey(conversationId))
      return null
    }
    return {
      version: 1,
      conversationId,
      text: parsed.text.slice(0, MAX_DRAFT_LENGTH),
      attachments: parsed.attachments.filter((item: unknown) => {
        if (!item || typeof item !== 'object') return false
        const attachment = item as Record<string, unknown>
        return typeof attachment.name === 'string' && typeof attachment.type === 'string'
          && ['api_document', 'endpoints_filter', 'identity_feed'].includes(String(attachment.kind))
      }).slice(0, 3),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    }
  } catch {
    return null
  }
}

export function saveConversationDraft(
  conversationId: string,
  text: string,
  attachments: DraftAttachmentReference[],
  storage: Storage = localStorage,
): DraftSaveResult {
  try {
    const key = conversationDraftKey(conversationId)
    if (!text.trim() && !attachments.length) {
      storage.removeItem(key)
      return 'cleared'
    }
    if (draftAppearsSensitive(text)) {
      storage.removeItem(key)
      return 'sensitive'
    }
    const draft: ConversationDraft = {
      version: 1,
      conversationId,
      text: text.slice(0, MAX_DRAFT_LENGTH),
      attachments: attachments.slice(0, 3),
      updatedAt: new Date().toISOString(),
    }
    storage.setItem(key, JSON.stringify(draft))
    return 'saved'
  } catch {
    return 'unavailable'
  }
}

export function clearConversationDraft(conversationId: string, storage: Storage = localStorage) {
  try { storage.removeItem(conversationDraftKey(conversationId)) } catch { /* unavailable storage */ }
}
